import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Part {
  id: string; code: string;
  part_number: string | null; oem_part_number: string | null;
  name_en: string; name_ar: string | null;
  category: string | null; unit: string;
  compatible_brands: string[] | null;
  last_purchase_price_sar: string | null;
  last_purchase_date: string | null;
  is_active: boolean;
  total_stock: string;
}

const fmtSar = (s: string | null) => 'SAR ' + Number(s ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

export default function SparePartsCatalogPage() {
  const [items, setItems] = useState<Part[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const r = await apiClient.get<Part[]>('/api/v1/maintenance/parts?' + params.toString());
      setItems(Array.isArray(r.data) ? r.data : []);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { refresh(); }, [refresh]);

  async function submitCreate() {
    if (!name) return;
    setSubmitting(true);
    try {
      await apiClient.post('/api/v1/maintenance/parts', {
        nameEn: name,
        partNumber: partNumber || undefined,
        category: category || undefined,
      });
      setCreating(false);
      setName(''); setPartNumber(''); setCategory('');
      await refresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/maintenance"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Maintenance</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wrench className="h-4 w-4" /> Spare parts catalog</div>
          <Button size="sm" onClick={() => setCreating(true)} className="gap-1"><Plus className="h-4 w-4" /> Add part</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-3">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or part number…" />
        {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        : error ? <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
        : items.length === 0 ? <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No parts in catalog.</div>
        : <div className="rounded-lg border border-border overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Part #</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-left px-3 py-2">Compatible</th>
                  <th className="text-right px-3 py-2">Last price</th>
                  <th className="text-right px-3 py-2">Stock</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 font-mono text-xs">{p.code}</td>
                    <td className="px-3 py-2">{p.name_en}{p.name_ar ? ' · ' + p.name_ar : ''}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.part_number ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">{p.category ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">{p.compatible_brands?.join(', ') ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{p.last_purchase_price_sar ? fmtSar(p.last_purchase_price_sar) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(p.total_stock ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </main>

      <Sheet open={creating} onOpenChange={setCreating}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Add part to catalog</SheetTitle>
            <SheetDescription>Spare-parts master record.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-3">
            <div className="space-y-1"><Label>Name (EN) *</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="space-y-1"><Label>Part number</Label><Input value={partNumber} onChange={(e) => setPartNumber(e.target.value)} placeholder="e.g. A0001234567" /></div>
            <div className="space-y-1"><Label>Category</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                <option value="">—</option>
                {['filter','brake','tire','fluid','electrical','engine','suspension','glass','body','other'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreating(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submitCreate} disabled={submitting || !name}>{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Create</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
